// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import Button from 'shared/components/common/Button';
import AddEditPOApprovalRuleModal from 'main-app/components/AddEditPOApprovalRuleModal';
import DeletePOApprovalRuleMutation from 'main-app/mutations/DeletePOApprovalRule';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  updateModalRecord: ?Object,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
`;

const ButtonWrapper = styled.div`
  padding: 16px 16px 24px 16px;

  @media (min-width: 1000px) {
    align-self: flex-end;
    padding: 0 0 24px;
  }
`;

const ListWrapper = styled.div`
  flex: 1;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const InfoBox = styled.div`
  margin: 0 0 24px 0;
  padding: 16px;
  background: #f5f5f5;
  border-left: 4px solid #4a90e2;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
`;

class POApprovalRules extends PureComponent<Props, State> {
  menuTriggerRefs = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      updateModalRecord: null,
    };
  }

  handleOpenUpdateModal = (target: *, ruleEdges: ?Array<Object>) => {
    const ruleId = target.getAttribute('rule-id');

    const rule = ruleEdges
      ? ruleEdges.find(({ node }) => node.id === ruleId)
      : null;

    if (rule) {
      this.setState({
        updateModalRecord: rule.node,
      });
    }
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      updateModalRecord: null,
    });
  };

  handleDeleteRule = (target: *) => {
    const ruleId = target.getAttribute('rule-id');

    if (
      window.confirm(
        i18n.t('Are you sure you want to delete this approval rule?')
      )
    ) {
      DeletePOApprovalRuleMutation.commit({
        variables: {
          input: {
            id: ruleId,
          },
        },
        onSuccess: () => {
          Actions.alertNotification(
            i18n.t('Approval rule successfully deleted'),
            'success'
          );
        },
        onFailure: error => {
          Actions.alertNotification(error.message, 'error');
        },
      });
    }
  };

  formatCurrency = (value: ?number) => {
    if (value === null || value === undefined) {
      return i18n.t('Unlimited');
    }
    return `${getCurrencySymbol()}${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  render() {
    const { history } = this.props;
    const { addModalOpen, updateModalRecord } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query POApprovalRulesQuery {
            poApprovalRules(first: null)
              @connection(key: "POApprovalRules_poApprovalRules", filters: []) {
              edges {
                node {
                  id
                  minAmount
                  maxAmount
                  approver {
                    id
                    firstName
                    lastName
                    email
                  }
                  createdAt
                }
              }
            }
          }
        `}
        variables={null}
        render={({ props }) => {
          if (!props) {
            return <Loader />;
          }

          const sortedRules = [...(props.poApprovalRules?.edges || [])]
            .filter(edge => edge && edge.node)
            .sort((a, b) => {
              const minA = a.node.minAmount || 0;
              const minB = b.node.minAmount || 0;
              return minA - minB;
            });

          return (
            <Wrapper>
              <InfoBox>
                <strong>{i18n.t('Purchase Order Approval Rules')}</strong>
                <div style={{ marginTop: 8 }}>
                  {i18n.t(
                    'Configure approval workflows based on purchase order amounts. POs below the lowest threshold will be auto-approved.'
                  )}
                </div>
              </InfoBox>

              <ButtonWrapper>
                <Button
                  onClick={this.handleOpenCreateModal}
                  theme="border-white"
                >
                  {i18n.t('Add Approval Rule')}
                </Button>
              </ButtonWrapper>

              {!sortedRules.length ? (
                <EmptyState>
                  <div style={{ marginBottom: 16 }}>
                    {i18n.t("You haven't created any approval rules yet.")}
                  </div>
                  <div style={{ fontSize: 14, color: '#999' }}>
                    {i18n.t(
                      'All purchase orders will be auto-approved until you create approval rules.'
                    )}
                  </div>
                </EmptyState>
              ) : (
                <ListWrapper>
                  <Table
                    data={sortedRules.map(edge => edge.node)}
                    columns={[
                      {
                        Header: i18n.t('Min Amount'),
                        accessor: 'minAmount',
                        width: 140,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {this.formatCurrency(value)}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Max Amount'),
                        accessor: 'maxAmount',
                        width: 140,
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {this.formatCurrency(value)}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Approver'),
                        accessor: 'approver',
                        Cell: ({ value }) => (
                          <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                            {value
                              ? `${value.firstName} ${value.lastName}`
                              : '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Email'),
                        accessor: 'approver.email',
                        Cell: ({ value }) => (
                          <div
                            style={{
                              paddingTop: 8,
                              paddingBottom: 8,
                              color: '#666',
                            }}
                          >
                            {value || '-'}
                          </div>
                        ),
                      },
                      {
                        Header: i18n.t('Rule Range'),
                        accessor: 'id',
                        Cell: ({ row }) => {
                          const { minAmount, maxAmount } = row.original;
                          let range = '';
                          if (minAmount !== null && maxAmount !== null) {
                            range = `${getCurrencySymbol()}${minAmount} - ${getCurrencySymbol()}${maxAmount}`;
                          } else if (minAmount !== null) {
                            range = `${getCurrencySymbol()}${minAmount}+`;
                          } else {
                            range = '-';
                          }
                          return (
                            <div
                              style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                                fontWeight: 600,
                                color: '#4a90e2',
                              }}
                            >
                              {range}
                            </div>
                          );
                        },
                      },
                      {
                        Header: '',
                        accessor: 'id',
                        width: 48,
                        enforceWidth: true,
                        Cell: ({ value }) => (
                          <ContextMenuTrigger
                            ref={r => (this.menuTriggerRefs[value] = r)}
                            id="settings-approval-rules-menu"
                            attributes={{
                              'rule-id': value,
                            }}
                          >
                            <Icon
                              type="circle-context-menu"
                              size={24}
                              onClick={e => {
                                if (this.menuTriggerRefs[value]) {
                                  this.menuTriggerRefs[
                                    value
                                  ].handleContextClick(e);
                                }
                              }}
                            />
                          </ContextMenuTrigger>
                        ),
                      },
                    ]}
                  />
                </ListWrapper>
              )}
              {addModalOpen && (
                <AddEditPOApprovalRuleModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  rule={null}
                />
              )}
              {updateModalRecord && (
                <AddEditPOApprovalRuleModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  rule={updateModalRecord}
                />
              )}
              <ContextMenu id="settings-approval-rules-menu">
                <MenuItem
                  onClick={(e, data, target) =>
                    this.handleOpenUpdateModal(
                      target,
                      props.poApprovalRules.edges
                    )
                  }
                >
                  {i18n.t('Edit Rule')}
                </MenuItem>
                <MenuItem
                  onClick={(e, data, target) => this.handleDeleteRule(target)}
                >
                  {i18n.t('Delete Rule')}
                </MenuItem>
              </ContextMenu>
            </Wrapper>
          );
        }}
      />
    );
  }
}

export default POApprovalRules;
